/* eslint-disable @typescript-eslint/indent */
import * as React from 'react';
import BatchActionModalLayout, {
  SelectedRow,
} from '@components/Layout/BatchActionModalLayout';
import {
  Box,
  OutlinedInput,
  TextareaAutosize,
  Typography,
  useTheme
} from '@mui/material';
import { CareerServicesApi } from '@api/CareerService.api';
import { BatchActionSubject, SendEmailBatchReq } from '@api/models/careerServicesApi.models';
import { styled } from '@mui/system';
import { BatchActionType, useBatchActionsContext } from '@common/context/batchActionsContext';
import { AxiosError } from 'axios';
import { Toast, useToast } from '@components/Toast';

interface SelectedRowExtended extends SelectedRow {
  managerEmail?: string;
  managerName?: string;
  managerFirstName?: string;
  managerLastName?: string;
  approver1Email?: string;
  approver1Name?: string;
  approver1FirstName?: string;
  approver1LastName?: string;
  approver2Email?: string;
  approver2Name?: string;
  approver2FirstName?: string;
  approver2LastName?: string;
  approver3Email?: string;
  approver3Name?: string;
  approver3FirstName?: string;
  approver3LastName?: string;
  approver1CanApprove?: boolean;
  approver2CanApprove?: boolean;
  approver3CanApprove?: boolean;
}

const Styled = {
  EmailBatchToLabel: styled(Typography)(({ theme }) => ({
    marginBottom: '5px',
    textTransform: 'uppercase',
    color: theme.palette.grey4.main
  }))
};

interface Props {
  open: boolean;
  batchActionType: BatchActionType;
}

const EmailBatchModal: React.FC<Props> = ({ open, batchActionType }) => {
  const { handleCloseModal } = useBatchActionsContext();
  const theme = useTheme();

  const { openToast, ...toastProps } = useToast();

  const [sendingEmail, setSendingEmail] = React.useState(false);
  const [emailSubject, setEmailSubject] = React.useState<string>('');
  const [emailBody, setEmailBody] = React.useState<string>('');
  const [selectedRows, setSelectedRows] = React.useState<SelectedRowExtended[]>([]);
  const [batchActionSubjects, setBatchActionSubjects] = React.useState<BatchActionSubject[]>([]);

  const getModalTitle = React.useMemo(() => {
    switch (batchActionType) {
      case BatchActionType.EMAIL_STUDENT:
        return 'Send Email to Students';
      case BatchActionType.EMAIL_MANAGER:
        return 'Send Email to Managers';
      case BatchActionType.EMAIL_APPROVERS:
        return 'Send Email to Timesheet Approvers';
      default:
        return 'Send Email to Students';
    }
  }, [batchActionType]);

  React.useMemo(() => {
    // Only generate Recipients from selected rows on modal open
    if (!open) return;

    const batchActionSubjectsMap = new Map<string, BatchActionSubject>();

    const addBatchActionSubjectToMap = (studentId: string, email: string, name: string, externshipId: number): void => {
      const batchActionSubject = batchActionSubjectsMap.get(email);

      if (batchActionSubject) {
        if (!batchActionSubject.externshipIds.includes(externshipId)) {
          batchActionSubject.externshipIds.push(externshipId);
        }
      } else {
        batchActionSubjectsMap.set(email, {
          studentId,
          name,
          email,
          externshipIds: [externshipId]
        });
      }
    };

    switch (batchActionType) {
      case BatchActionType.EMAIL_STUDENT:
        selectedRows.forEach((row) => {
          // Try multiple possible field names for student email
          if (row.studentEmail && row.studentName) {
            addBatchActionSubjectToMap(row.studentId, row.studentEmail, row.studentName, row.externshipId);
          }
        });
        if (!batchActionSubjectsMap.size) openToast('No rows selected!');
        break;

      case BatchActionType.EMAIL_MANAGER:
        selectedRows.forEach((row) => {
          if (row.managerEmail && row.managerName) {
            addBatchActionSubjectToMap(row.studentId, row.managerEmail, row.managerName, row.externshipId);
          }
        });

        if (!batchActionSubjectsMap.size) openToast('Selected rows do not have any managers!');
        break;

      case BatchActionType.EMAIL_APPROVERS:
        selectedRows.forEach((row) => {
          // Try multiple field name patterns for approvers
          for (let i = 1; i <= 3; i++) {
            const approverName = row[`approver${i}Name`];
            const approverEmail = row[`approver${i}Email`];

            if (approverEmail && approverName) {
              addBatchActionSubjectToMap(row.studentId, approverEmail, approverName, row.externshipId);
            }
          }
        });

        if (!batchActionSubjectsMap.size) openToast('Selected rows do not have any approvers!');
        break;
    }

    setBatchActionSubjects(Array.from(batchActionSubjectsMap.values()));
  }, [open]);

  const successfulToastMessage = React.useMemo(() => {
    switch (batchActionType) {
      case BatchActionType.EMAIL_MANAGER:
        return 'Email sent to selected manager(s)';
      case BatchActionType.EMAIL_APPROVERS:
        return 'Email sent to selected timesheet approver(s)';
      case BatchActionType.EMAIL_STUDENT:
      default:
        return 'Email sent to selected student(s)';
    }
  }, [batchActionType]);

  const handleEmailBatchBtn = async (): Promise<void> => {
    const postBody: SendEmailBatchReq = {
      batchActionType: batchActionType,
      batchActionSubjects: batchActionSubjects,
      batchActionEmail: {
        subject: emailSubject,
        body: emailBody
      }
    };

    try {
      setSendingEmail(true);
      await CareerServicesApi.sendEmailBatch(postBody);

      openToast(successfulToastMessage);
      setEmailBody('');
      setEmailSubject('');
      handleCloseModal();
    } catch (error: any) {
      let message = 'Something went wrong';

      if (error.response?.data?.message) {
        message = error.response?.data?.message;
      } else if (error instanceof AxiosError) {
        message = error.message;
      }
      openToast(`Error sending request: ${message}`);
      console.error('Error: CareerServicesDashboardPage.handleEmailBatchBtn()', error);
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <BatchActionModalLayout
      open={open}
      modalTitle={getModalTitle}
      primaryBtnText="Send"
      maxTagsToDisplay={27}
      setSelectedRows={setSelectedRows}
      handleSubmitClick={handleEmailBatchBtn}
      handleClose={handleCloseModal}
      loading={sendingEmail}
      batchActionSubjects={batchActionSubjects}
      setBatchActionSubjects={setBatchActionSubjects}
    >
      <Box display="grid">
        <Styled.EmailBatchToLabel variant="EC_TYPE_XS">
          RE:
        </Styled.EmailBatchToLabel>
        <OutlinedInput
          value={emailSubject}
          onChange={(e): void => setEmailSubject(e.target.value)}
          placeholder="Subject"
        />

        <Styled.EmailBatchToLabel variant="EC_TYPE_XS" paddingTop={'25px'}>
          EMAIL BODY:
        </Styled.EmailBatchToLabel>
        <TextareaAutosize
          value={emailBody}
          onChange={(e): void => setEmailBody(e.target.value)}
          minRows={3}
          style={{
            borderColor: theme.palette.OUTLINE.main,
            borderRadius: '5px',
            padding: '10px',
            font: 'inherit'
          }}
        />
      </Box>
      <Toast {...toastProps} />
    </BatchActionModalLayout>
  );
};

export default EmailBatchModal;
